import {
  Card,
  Button,
  Form,
  Alert,
  Spinner,
  ListGroup,
  Modal,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React , {useState, useEffect, useRef} from "react";
import {
  faArrowRight,
  faTrash,
  faUpload,
  faTimes,
  faCopy,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import QRCode from "qrcode";
import Cookies from "js-cookie";
import styles from "../../Import/ImportCSS.json"; // reuse Import styles

// ErrorAlert (red frame)
const ErrorAlert = ({ error, t }) =>
  error ? (
    <Alert
      variant="danger"
      style={{
        width: "90%",
        maxWidth: 600,
        margin: "20px auto 0 auto",
        padding: "2%",
      }}
    >
      {error}
    </Alert>
  ) : null;

// StepError (red frame + actions)
export const StepError = ({ error, t, setStep, finalRoute, navigate }) => (
  <>
    <Card
      className="text-center"
      style={{
        ...styles.CardStyles,
        width: "90%",
        maxWidth: "600px",
        margin: "0 auto",
      }}
    >
      <Card.Body>
        {t("Error in processing, please try again")}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "10px",
            marginTop: 16,
          }}
        >
          <Button
            className="btn-success"
            onClick={() => navigate(finalRoute || "/")}
          >
            {t("Return instance")}
          </Button>
          <Button className="btn-success" onClick={() => setStep(1)}>
            {t("Add instance")}
          </Button>
        </div>
      </Card.Body>
    </Card>
    {error && <ErrorAlert error={error} t={t} />}
  </>
);

/* StepBar — steps mapped to numbers 1..7 (6 étapes + succès) */
export const StepBar = ({ step, isIndividualPath, t }) => {
  // labels depend on path
  const addressLabel = isIndividualPath ? t("Address") : t("KBIS");
  const revenueLabel = isIndividualPath
    ? t("Proof of Revenue")
    : t("Procuration (optional)");

  const steps = [
    t("Choice"),
    t("Info"),
    addressLabel,
    revenueLabel,
    t("Verification of Identity"),
    t("Finish"),
  ];

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: 30,
      }}
    >
      {steps.map((label, idx) => {
        const stepNumber = idx + 1;
        const isActive = step === stepNumber;
        const isDone = step > stepNumber;
        return (
          <div
            key={label}
            style={{ display: "flex", alignItems: "center", margin: 5 }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: isActive
                  ? "#3c6382"
                  : isDone
                  ? "#78e08f"
                  : "#d1d8e0",
                color: isActive || isDone ? "#fff" : "#636e72",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: 16,
                border: isActive ? "2px solid #3c6382" : "2px solid #d1d8e0",
                transition: "background 0.3s",
              }}
            >
              {stepNumber}
            </div>
            <div
              style={{
                marginLeft: 8,
                marginRight: 8,
                color: isActive ? "#3c6382" : "#636e72",
                fontWeight: isActive ? "bold" : "normal",
                fontSize: 14,
                maxWidth: 200,
                textAlign: "center",
              }}
            >
              {label}
            </div>
            {idx < steps.length - 1 && <div style={{ width: 32, height: 2 }} />}
          </div>
        );
      })}
    </div>
  );
};

// Loading modal
export const LoadingModal = ({ loading, t }) => (
  <Modal show={loading} centered backdrop="static">
    <Modal.Body className="text-center">
      <Spinner animation="border" role="status">
        <span className="visually-hidden">{t("Loading")}...</span>
      </Spinner>
      <h4>{t("Processing in progress")}...</h4>
    </Modal.Body>
  </Modal>
);

// Step components — use ErrorAlert when state.error present (no separate missing-field modal)
export const Step0Model = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { modelList, selectedModel, error } = state;
  const { setSelectedModel, handleNext } = handlers;
  return (
    <>
      <Card
        className="text-center"
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: "600px",
          margin: "0 auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Choose a model")}
          </Card.Title>
          <Form.Select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            style={{ width: "100%", maxWidth: 400, margin: "0 auto" }}
          >
            <option value="All">{t("Choice of model")}</option>
            {modelList.map((m) => (
              <option key={m.ModelId} value={m.ModelId}>
                {m.ModelName}
              </option>
            ))}
          </Form.Select>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginTop: 16,
            }}
          >
            <Button
              className="btn"
              style={styles.NextButton}
              onClick={handleNext}
            >
              {t("Next")} <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </Card.Body>
      </Card>
      <ErrorAlert error={error} t={t} />
    </>
  );
};

// The KYC cards (info / address / revenue / kbis / procuration) components
export const Step0Choice = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { error } = state;
  const { setChoiceAndNext } = handlers;
  return (
    <>
      <Card
        className="text-center"
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: 600,
          margin: "50px auto",
          padding: "40px 20px",
          borderRadius: "20px",
          boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
          background: "#f9f9f9",
        }}
      >
        <Card.Body>
          <Card.Title style={{ fontSize: "2rem", color: "#34495e" }}>
            {t("Choice")}
          </Card.Title>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              marginTop: "30px",
            }}
          >
            <Button
              style={{
                flex: 1,
                padding: "18px 0",
                fontSize: "1.6rem",
                fontWeight: "600",
                borderRadius: "15px",
                border: "none",
                color: "#fff",
                background: "linear-gradient(135deg, #1d8314ff, #83c434ff)",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.filter = "brightness(1.1)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.filter = "brightness(1)")
              }
              onClick={() => setChoiceAndNext(true)}
            >
              {t("Particulier")}
            </Button>

            <Button
              style={{
                flex: 1,
                padding: "18px 0",
                fontSize: "1.6rem",
                fontWeight: "600",
                borderRadius: "15px",
                border: "none",
                color: "#fff",
                background: "linear-gradient(135deg, #6a11cb, #2575fc)",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.filter = "brightness(1.1)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.filter = "brightness(1)")
              }
              onClick={() => setChoiceAndNext(false)}
            >
              {t("Entreprise")}
            </Button>
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

export const Step1IndividualInfo = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { individualFirstName, individualLastName, individualDOB, error } =
    state;
  const {
    setIndividualFirstName,
    setIndividualLastName,
    setIndividualDOB,
    handlePrevious,
    handleNext,
  } = handlers;

  return (
    <>
      <Card
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: 700,
          margin: "0 auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Personal Info")}
          </Card.Title>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>{t("First name")}</Form.Label>
              <Form.Control
                value={individualFirstName}
                onChange={(e) => setIndividualFirstName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t("Last name")}</Form.Label>
              <Form.Control
                value={individualLastName}
                onChange={(e) => setIndividualLastName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t("Date of birth")}</Form.Label>
              <Form.Control
                type="date"
                value={individualDOB}
                onChange={(e) => setIndividualDOB(e.target.value)}
              />
            </Form.Group>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 10,
                marginTop: 16,
              }}
            >
              <Button variant="secondary" onClick={handlePrevious}>
                {t("Previous")}
              </Button>
              <Button style={styles.NextButton} onClick={handleNext}>
                {t("Next")} <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

export const Step2IndividualDomicile = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { addressFileName, error } = state;
  const {
    handleDragOver,
    handleDrop,
    openPickerFor,
    handleDeleteStepFile,
    handlePrevious,
    handleNext,
    setPickerTarget,
  } = handlers;

  return (
    <>
      <Card
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: 700,
          margin: "0 auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Proof of Address")}
          </Card.Title>

          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnter={() => setPickerTarget("address")}
            style={{
              border: "2px dashed #d1d8e0",
              padding: 20,
              borderRadius: 8,
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => openPickerFor("address")}
          >
            <FontAwesomeIcon icon={faUpload} size="2x" />
            <div style={{ marginTop: 8 }}>
              {addressFileName
                ? addressFileName
                : t("Drop or click to upload proof of address")}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginTop: 16,
            }}
          >
            <Button variant="secondary" onClick={handlePrevious}>
              {t("Previous")}
            </Button>
            <Button style={styles.NextButton} onClick={handleNext}>
              {t("Next")} <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            {addressFileName && (
              <Button
                variant="danger"
                onClick={() => handleDeleteStepFile("address")}
              >
                <FontAwesomeIcon icon={faTrash} /> {t("Remove")}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

export const Step3IndividualRevenue = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { revenueFileName, error } = state;
  const {
    handleDragOver,
    handleDrop,
    openPickerFor,
    handleDeleteStepFile,
    handlePrevious,
    handleNext,
    setPickerTarget,
  } = handlers;

  return (
    <>
      <Card
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: 700,
          margin: "0 auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Proof of Revenue")}
          </Card.Title>

          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnter={() => setPickerTarget("revenue")}
            style={{
              border: "2px dashed #d1d8e0",
              padding: 20,
              borderRadius: 8,
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => openPickerFor("revenue")}
          >
            <FontAwesomeIcon icon={faUpload} size="2x" />
            <div style={{ marginTop: 8 }}>
              {revenueFileName
                ? revenueFileName
                : t("Drop or click to upload proof of revenue")}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginTop: 16,
            }}
          >
            <Button variant="secondary" onClick={handlePrevious}>
              {t("Previous")}
            </Button>
            <Button style={styles.NextButton} onClick={handleNext}>
              {t("Next")} <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            {revenueFileName && (
              <Button
                variant="danger"
                onClick={() => handleDeleteStepFile("revenue")}
              >
                <FontAwesomeIcon icon={faTrash} /> {t("Remove")}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

export const Step1CompanyInfo = ({ state, handlers, computed }) => {
  const { t } = computed;
  const {
    companyName,
    companyRepresentativeFirstName,
    companyRepresentativeName,
    companyRCS,
    error,
  } = state;
  const {
    setCompanyName,
    setCompanyRepresentativeFirstName,
    setCompanyRepresentativeName,
    setCompanyRCS,
    handlePrevious,
    handleNext,
  } = handlers;

  return (
    <>
      <Card
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: 700,
          margin: "0 auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Company Info")}
          </Card.Title>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>{t("Company name")}</Form.Label>
              <Form.Control
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t("Representative first name")}</Form.Label>
              <Form.Control
                value={companyRepresentativeFirstName}
                onChange={(e) =>
                  setCompanyRepresentativeFirstName(e.target.value)
                }
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t("Representative last name")}</Form.Label>
              <Form.Control
                value={companyRepresentativeName}
                onChange={(e) => setCompanyRepresentativeName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t("RCS")}</Form.Label>
              <Form.Control
                value={companyRCS}
                onChange={(e) => setCompanyRCS(e.target.value)}
              />
            </Form.Group>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 10,
                marginTop: 16,
              }}
            >
              <Button variant="secondary" onClick={handlePrevious}>
                {t("Previous")}
              </Button>
              <Button style={styles.NextButton} onClick={handleNext}>
                {t("Next")} <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

export const Step2CompanyKBIS = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { kbisFileName, error } = state;
  const {
    handleDragOver,
    handleDrop,
    openPickerFor,
    handleDeleteStepFile,
    handlePrevious,
    handleNext,
    setPickerTarget,
  } = handlers;

  return (
    <>
      <Card
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: 700,
          margin: "0 auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>{t("KBIS")}</Card.Title>

          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnter={() => setPickerTarget("kbis")}
            style={{
              border: "2px dashed #d1d8e0",
              padding: 20,
              borderRadius: 8,
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => openPickerFor("kbis")}
          >
            <FontAwesomeIcon icon={faUpload} size="2x" />
            <div style={{ marginTop: 8 }}>
              {kbisFileName ? kbisFileName : t("Drop or click to upload KBIS")}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginTop: 16,
            }}
          >
            <Button variant="secondary" onClick={handlePrevious}>
              {t("Previous")}
            </Button>
            <Button style={styles.NextButton} onClick={handleNext}>
              {t("Next")} <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            {kbisFileName && (
              <Button
                variant="danger"
                onClick={() => handleDeleteStepFile("kbis")}
              >
                <FontAwesomeIcon icon={faTrash} /> {t("Remove")}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

export const Step3CompanyProcuration = ({ state, handlers, computed }) => {
  const { t } = computed;
  const { hasProcuration, procurationFileName, error } = state;
  const {
    setHasProcuration,
    handleDragOver,
    handleDrop,
    openPickerFor,
    handleDeleteStepFile,
    handlePrevious,
    handleNext,
    setPickerTarget,
  } = handlers;

  return (
    <>
      <Card
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: 700,
          margin: "0 auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Procuration (optional)")}
          </Card.Title>

          <Form.Check
            type="checkbox"
            label={t("Has procuration?")}
            checked={!!hasProcuration}
            onChange={(e) => setHasProcuration(e.target.checked)}
          />

          {hasProcuration && (
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnter={() => setPickerTarget("procuration")}
              style={{
                border: "2px dashed #d1d8e0",
                padding: 20,
                borderRadius: 8,
                textAlign: "center",
                cursor: "pointer",
                marginTop: 12,
              }}
              onClick={() => openPickerFor("procuration")}
            >
              <FontAwesomeIcon icon={faUpload} size="2x" />
              <div style={{ marginTop: 8 }}>
                {procurationFileName
                  ? procurationFileName
                  : t("Drop or click to upload procuration")}
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginTop: 16,
            }}
          >
            <Button variant="secondary" onClick={handlePrevious}>
              {t("Previous")}
            </Button>
            <Button style={styles.NextButton} onClick={handleNext}>
              {t("Next")} <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            {procurationFileName && (
              <Button
                variant="danger"
                onClick={() => handleDeleteStepFile("procuration")}
              >
                <FontAwesomeIcon icon={faTrash} /> {t("Remove")}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

// StepFinalDocuments component for uploading final documents
export const StepFinalDocuments = ({
  state,
  handlers,
  computed,
  fileInputRef,
}) => {
  const { t } = computed;
  const {
    selectedFiles,
    droppedFileNames,
    error,
    addressFileName,
    revenueFileName,
    kbisFileName,
    procurationFileName,
    hasProcuration,
  } = state;

  const {
    handleDragOver,
    handleDrop,
    openPickerFor,
    handleDeleteSpecificFile,
    handlePrevious,
    handleNext,
  } = handlers;

  return (
    <>
      <Card
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: 900,
          margin: "0 auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>{t("Documents")}</Card.Title>

          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{
              border: "2px dashed #d1d8e0",
              padding: 24,
              borderRadius: 8,
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => openPickerFor("final")}
          >
            <FontAwesomeIcon icon={faUpload} size="2x" />
            <div style={{ marginTop: 8 }}>
              {t("Drop or click to upload final documents")}
            </div>
          </div>

          {/* Files gathered from previous steps - read-only */}
          <ListGroup style={{ marginTop: 16 }}>
            {addressFileName ? (
              <ListGroup.Item
                variant="light"
                key="address"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  opacity: 0.85,
                }}
              >
                <div>
                  {t("Address")}: {addressFileName}
                </div>
                <div style={{ color: "#999", fontSize: 12 }}>
                  {t("Uploaded in step")}
                </div>
              </ListGroup.Item>
            ) : null}

            {kbisFileName ? (
              <ListGroup.Item
                variant="light"
                key="kbis"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  opacity: 0.85,
                }}
              >
                <div>
                  {t("KBIS")}: {kbisFileName}
                </div>
                <div style={{ color: "#999", fontSize: 12 }}>
                  {t("Uploaded in step")}
                </div>
              </ListGroup.Item>
            ) : null}

            {revenueFileName ? (
              <ListGroup.Item
                variant="light"
                key="revenue"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  opacity: 0.85,
                }}
              >
                <div>
                  {t("Proof of Revenue")}: {revenueFileName}
                </div>
                <div style={{ color: "#999", fontSize: 12 }}>
                  {t("Uploaded in step")}
                </div>
              </ListGroup.Item>
            ) : null}

            {hasProcuration && procurationFileName ? (
              <ListGroup.Item
                variant="light"
                key="procuration"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  opacity: 0.85,
                }}
              >
                <div>
                  {t("Procuration")}: {procurationFileName}
                </div>
                <div style={{ color: "#999", fontSize: 12 }}>
                  {t("Uploaded in step")}
                </div>
              </ListGroup.Item>
            ) : null}

            {/* Newly added files in Documents step — can be deleted */}
            {droppedFileNames &&
              droppedFileNames.length > 0 &&
              droppedFileNames.map((name, i) => (
                <ListGroup.Item
                  key={`new-${i}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>{name}</div>
                  <div>
                    <Button
                      variant="light"
                      onClick={() => handleDeleteSpecificFile(i)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}

            {(!droppedFileNames || droppedFileNames.length === 0) &&
              !addressFileName &&
              !kbisFileName &&
              !revenueFileName &&
              !(hasProcuration && procurationFileName) && (
                <div style={{ textAlign: "center", marginTop: 12 }}>
                  {t("No files yet")}
                </div>
              )}
          </ListGroup>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginTop: 16,
            }}
          >
            <Button variant="secondary" onClick={handlePrevious}>
              {t("Previous")}
            </Button>
            {/* Le bouton "Submit" (étape 5) appelle maintenant handleNext pour lancer handleSubmitDocuments et passer à l'étape 6 */}
            <Button style={styles.NextButton} onClick={handleNext}>
              {t("Submit")} <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </Card.Body>
      </Card>
      {error && <ErrorAlert error={error} t={t} />}
    </>
  );
};

// Step6LocalLink component to redirect to SecureModal page
export const Step6LocalLink = ({ state, handlers, computed }) => {
  const { t } = computed;
  const {
    showQR,
    secureUrl,
    qrDataUrl,
  } = state;

  const {
    handlePrevious,
    generateSecureLink,
    copyLink,
    closeQR,
  } = handlers;

  return (
    <>
      <Card
        style={{
          ...styles.CardStyles,
          width: "90%",
          maxWidth: 700,
          margin: "0 auto",
        }}
      >
        <Card.Body>
          <Card.Title style={{ color: "#3c6382" }}>
            {t("Open Secure Modal")}
          </Card.Title>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                textAlign: "center",
                fontSize: 14,
                color: "#444",
                wordBreak: "break-word",
              }}
            >
              {t(
                "Please open the Secure verification page to continue. You can scan the QR code shown to continue on another device."
              )}
            </div>

            <Button
              variant="outline-primary"
              style={{ marginTop: 8 }}
              onClick={generateSecureLink}
            >
              {t("Open Secure Modal / Generate QR")}
            </Button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginTop: 16,
            }}
          >
            <Button className="btn btn-secondary" onClick={handlePrevious}>
              {t("Previous")}
            </Button>
            <Button
              className="btn"
              style={styles.NextButton}
              onClick={generateSecureLink}
            >
              {t("Go to verification")}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={showQR}
        onHide={null}
        centered
        size="md"
        dialogClassName="p-0"
        backdropClassName="bg-dark bg-opacity-50"
        keyboard={false} 
        backdrop="static"
      >
        <Modal.Header
          className="border-0 position-relative p-3"
          style={{ justifyContent: "center" }}
        >
        
          <Modal.Title
            style={{ fontWeight: 600, fontSize: 18, textAlign: "center" }}
          >
            {t("Secure Verification Link")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ textAlign: "center", padding: "1.5rem" }}>
          {secureUrl && (
            <>
              <div
                style={{
                  display: "inline-block",
                  padding: 12,
                  borderRadius: 12,
                  background: "#fff",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                  marginBottom: 20,
                }}
              >
                {qrDataUrl ? (
                  <img alt="QR code" src={qrDataUrl} style={{ width: 260, height: 260 }} />
                ) : (
                  <div style={{ width: 260, height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {t("Generating...")}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 16,
                  marginTop: 16,
                }}
              >
                
                <Button
                  variant="outline-secondary"
                  onClick={copyLink}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    minWidth: 120,
                  }}
                >
                  <FontAwesomeIcon icon={faCopy} />
                  {t("Copy link")}
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {state.error && <ErrorAlert error={state.error} t={t} />}
    </>
  );
};


// StepSuccess component to show success message and navigation options
export const StepSuccess = ({ computed, navigate, handlers }) => {
  const { t, finalRoute } = computed;
  const { setStep } = handlers;

  return (
    <Card
      className="text-center"
      style={{
        ...styles.CardStyles,
        width: "90%",
        maxWidth: 700,
        margin: "0 auto",
      }}
    >
      <Card.Body>
        <h3 style={{ color: "#3c6382" }}>{t("Success")}</h3>
        <p>
          {t("Your instance has been created and files uploaded successfully.")}
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
          <Button onClick={() => navigate(finalRoute || "/")}>
            {t("Go to instance")}
          </Button>
          <Button onClick={() => setStep(1)}>{t("Add another")}</Button>
        </div>
      </Card.Body>
    </Card>
  );
};
